import { Component, Input, OnInit } from '@angular/core';
import { Participant } from '@app/shared/models/participant';
import { SelectComponentOptions } from '@app/shared/components/select/base-select-with-id.component';
import { CaseParticipantUriBuilderParams, ParticipantsService } from '@app/core/services/participants.service';
import { ComponentServiceBundle } from '@app/core/services/base/component-service-bundle.service';
import { ParticipantType, ParticipantTypes } from '@app/shared/models/participant-type';
import { ParticipantComponent } from '@app/modules/case/components/participant-base/participant.component';
import { OpData } from '@app/core/services/base/base-service-with-bundle.service';

export enum AddressParticipantOptions {
  TITLE,
  NAME,
  MULTI_NAMES,
  ADDRESS,
  COUNTRY,
  EMAIL,
  PHONE,
  PHONE2,
  FAX,
  COMMENT,
  WITH_SEARCH
}

@Component({
  selector: 'address-participant',
  templateUrl: './address-participant.component.html',
  styleUrls: ['./address-participant.component.scss']
})
export class AddressParticipantComponent extends ParticipantComponent
                                         implements OnInit {

  // There are 3 options to work with this component
  // 1. You add the name of the id found in url in the property paramsIdNames (just as a string).
  //    => Component fetches the participant during unit via the ID
  //    => Component patches the participant via ID
  // 2. You pass a participant object
  //    => Component DOES NOT fetch the participant during unit
  // 3. you pass a participant type
  //    => Component fetches the participant during unit via the TYPE
  //    => Component patches the participant via TYPE

  // !! DO NOT TRY TO COMBINE METHOD 1, 2 or 3 !!

  get participant() {
    return this.updated;
  }

  @Input()
  set type(type: ParticipantType | string) {
    if (type) {
      const key = type instanceof ParticipantType ?  type.type : type;
      this.patchParams = {
        urlSuffix: key
      };
      this.myConfig = { fetchAtInit: true };
      this.setTranslateTypeForLabel(key);
      this.getOriginal();
    }
  }

  @Input()
  public config: AddressParticipantOptions[];

  @Input()
  private typeInLabels?: boolean;

  public countrySelectOptions: SelectComponentOptions;
  public withTitle: boolean;
  public withName: boolean;
  public withMultiNames: boolean;
  public withAddress: boolean;
  public withCountry: boolean;
  public withEmail: boolean;
  public withPhone: boolean;
  public withPhone2: boolean;
  public withFax: boolean;
  public withComment: boolean;
  public withSearch: boolean;

  // Only usable when the participant is fetched via the type,
  // Because only then do we know the type which is intended (since it can have several types)
  private translatedTypeForLabel: string;
  get typeForLabel(): string {
    return this.typeInLabels ? this.translatedTypeForLabel : '';
  }

  get participantTitle() {
    return this.updated.title || '';
  }

  set participantTitle(newVal: any) {
    this.updated.title = newVal ? newVal : null;
  }

  constructor(protected componentServiceBundle: ComponentServiceBundle,
              protected participantsService: ParticipantsService) {
    super(componentServiceBundle, participantsService);
    this.myConfig = { fetchAtInit: true };
  }

  public ngOnInit() {
    this.checkOptions();
    this.countrySelectOptions = {
      patchPath: 'address', // prefix for /country/code and /country/value
    };
  }

  protected whenOriginalFetched(): void {
    super.whenOriginalFetched();

    if (this.patchParams.urlSuffix && !this.original.isOfType(this.patchParams.urlSuffix)) {
      this.original.type.unshift(this.patchParams.urlSuffix);
      this.updated.type.unshift(this.patchParams.urlSuffix);
    }
  }

  private checkOptions(): void {
    if (this.config) {
      this.withTitle = this.config.indexOf(AddressParticipantOptions.TITLE) > -1;
      this.withName = this.config.indexOf(AddressParticipantOptions.NAME) > -1;
      this.withMultiNames = this.config.indexOf(AddressParticipantOptions.MULTI_NAMES) > -1;
      this.withAddress = this.config.indexOf(AddressParticipantOptions.ADDRESS) > -1;
      this.withCountry = this.config.indexOf(AddressParticipantOptions.COUNTRY) > -1;
      this.withEmail = this.config.indexOf(AddressParticipantOptions.EMAIL) > -1;
      this.withPhone = this.config.indexOf(AddressParticipantOptions.PHONE) > -1;
      this.withPhone2 = this.config.indexOf(AddressParticipantOptions.PHONE2) > -1;
      this.withFax = this.config.indexOf(AddressParticipantOptions.FAX) > -1;
      this.withComment = this.config.indexOf(AddressParticipantOptions.COMMENT) > -1;
      this.withSearch = this.config.indexOf(AddressParticipantOptions.WITH_SEARCH) > -1;
    }
  }

  private setTranslateTypeForLabel(typeKey: string): void {
    if (typeKey) {
      const translation = this.translateService.instant(ParticipantTypes.getTypeByKey(typeKey).trans);
      this.translatedTypeForLabel = `${translation} - `;
    }
  }

  private sendUpdateToParent(): void {
      this.bananaBoxChange.emit(new Participant(this.participant));
  }

  public searchBtnClicked(): void {

  }

  public patchOpDataArray(patchDataArray: OpData[], callbackFn?: Function): void {
    const self = this;
    super.patchOpDataArray(patchDataArray, (callbackFn || function() {
      self.sendUpdateToParent();
    }));
  }

  protected generateUriBuilderParams(): CaseParticipantUriBuilderParams {
    // If we have patchParams this means we are patching via type and not via ID
    const caseId = this.original && this.original.case_id ? this.original.case_id : this.caseId;
    const participantId = this.patchParams ? null : this.original.id;
    return new CaseParticipantUriBuilderParams(caseId, participantId);
  }
}
